import { Nav } from "../components/Nav";
import { Logo } from "../components/Logo";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, Cookie } from "lucide-react";
import { VersionHistory } from "../components/VersionHistory";
import { LegalLanguageSwitcher } from "../components/LegalLanguageSwitcher";

const COOKIE_TYPES = [
    {
        title: "Essential Cookies",
        body: "These cookies are necessary for the operation of SpLink.pro. They enable core features such as account login, session management, security, and website functionality.",
        required: true,
    },
    {
        title: "Performance and Analytics Cookies",
        body: "These cookies help us understand how visitors interact with our website by collecting anonymous or aggregated information, such as page views, browser types, device information, and usage statistics.",
    },
    {
        title: "Functional Cookies",
        body: "These cookies remember your preferences, such as language, region, and other settings, to provide a more personalized experience.",
    },
    {
        title: "Security Cookies",
        body: "Security cookies help protect your account and our services by detecting suspicious activity, preventing unauthorized access, and supporting fraud prevention measures.",
    },
    {
        title: "Third-Party Cookies",
        body: "Some features of SpLink.pro may rely on trusted third-party service providers, such as analytics, payment processors, or embedded services. These providers may set their own cookies, which are governed by their respective privacy and cookie policies.",
    },
];

const SECTIONS = [
    { id: "what",    title: "What Are Cookies?" },
    { id: "how",     title: "How We Use Cookies" },
    { id: "types",   title: "Types of Cookies We Use" },
    { id: "manage",  title: "Managing Cookies" },
    { id: "changes", title: "Changes to This Cookie Policy" },
    { id: "contact", title: "Contact Us" },
];

const USES = [
    "Keep you signed in to your account.",
    "Remember your preferences and settings.",
    "Improve website performance and functionality.",
    "Understand how visitors use our website.",
    "Measure the effectiveness of our services and features.",
    "Help detect fraud, abuse, and security threats.",
    "Maintain the reliability and security of our platform.",
];

const CONTROLS = [
    "View stored cookies.",
    "Delete existing cookies.",
    "Block all or selected cookies.",
    "Receive notifications before cookies are stored.",
];

export default function Cookies() {
    return (
        <div className="App min-h-screen">
            <Nav />
            <LegalLanguageSwitcher doc="cookies">

            {/* Hero */}
            <section className="relative overflow-hidden border-b border-border">
                <div className="pointer-events-none absolute inset-0 grid-lines opacity-40" aria-hidden />
                <div className="pointer-events-none absolute -right-32 top-0 h-96 w-96 rounded-full bg-[hsl(27,96%,61%)]/10 blur-3xl" aria-hidden />
                <div className="relative mx-auto max-w-4xl px-6 pb-14 pt-24">
                    <Link to="/" className="mb-8 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="cookies-back-link">
                        <ArrowLeft className="h-4 w-4" /> Back to home
                    </Link>
                    <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium uppercase tracking-widest text-muted-foreground" data-testid="cookies-badge">
                        <Cookie className="h-3 w-3 text-[hsl(27,96%,55%)]" />
                        Legal
                    </div>
                    <h1 className="mt-6 font-display text-5xl font-extrabold leading-[0.95] tracking-tighter sm:text-6xl" data-testid="cookies-heading">
                        SpLink.pro™<br />
                        <span className="brand-gradient-text">Cookie Policy</span>
                    </h1>
                    <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 font-mono text-xs text-muted-foreground">
                        <div>
                            <span className="text-foreground/60">Effective date:</span>{" "}
                            <span className="ml-1">August 4, 2026</span>
                        </div>
                        <div>
                            <span className="text-foreground/60">Last updated:</span>{" "}
                            <span className="ml-1">August 5, 2026</span>
                        </div>
                    </div>
                    <p className="mt-8 max-w-3xl text-base leading-relaxed text-muted-foreground">
                        This Cookie Policy explains how <strong className="text-foreground">SpLink.pro™</strong> ("SpLink.pro," "we," "our," or "us"), part of the SocialPulse ecosystem, uses cookies and similar technologies when you visit our website or use our services.
                    </p>
                    <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">
                        By continuing to use SpLink.pro, you consent to the use of cookies as described in this Cookie Policy, unless you disable them through your browser or cookie preferences where available.
                    </p>
                </div>
            </section>

            {/* TOC + content */}
            <section className="border-b border-border">
                <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-6 py-16 lg:grid-cols-[240px_1fr]">
                    <aside className="hidden lg:block">
                        <div className="sticky top-24">
                            <div className="mb-3 text-xs uppercase tracking-widest text-muted-foreground">Contents</div>
                            <nav className="flex flex-col gap-1.5 font-mono text-xs">
                                {SECTIONS.map((s, i) => (
                                    <a
                                        key={s.id}
                                        href={`#section-${s.id}`}
                                        className="text-muted-foreground hover:text-foreground"
                                        data-testid={`toc-link-${s.id}`}
                                    >
                                        <span className="text-[hsl(27,96%,55%)]">{String(i + 1).padStart(2, "0")}</span>{" "}
                                        {s.title}
                                    </a>
                                ))}
                            </nav>
                        </div>
                    </aside>

                    <article className="max-w-3xl">
                        {/* What are cookies */}
                        <section id="section-what" className="border-b border-border py-8 first:pt-0" data-testid="cookies-section-what">
                            <div className="text-xs uppercase tracking-widest text-muted-foreground">Section 01</div>
                            <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">What Are Cookies?</h2>
                            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                                Cookies are small text files that are stored on your computer, smartphone, or other device when you visit a website. They help websites remember your preferences, improve functionality, and provide a better user experience.
                            </p>
                        </section>

                        {/* How we use */}
                        <section id="section-how" className="border-b border-border py-8" data-testid="cookies-section-how">
                            <div className="text-xs uppercase tracking-widest text-muted-foreground">Section 02</div>
                            <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">How We Use Cookies</h2>
                            <p className="mt-4 text-base leading-relaxed text-muted-foreground">We use cookies to:</p>
                            <ul className="mt-4 space-y-2">
                                {USES.map((u) => (
                                    <li key={u} className="flex gap-3 text-base text-muted-foreground">
                                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(27,96%,55%)]" />
                                        {u}
                                    </li>
                                ))}
                            </ul>
                        </section>

                        {/* Types */}
                        <section id="section-types" className="border-b border-border py-8" data-testid="cookies-section-types">
                            <div className="text-xs uppercase tracking-widest text-muted-foreground">Section 03</div>
                            <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">Types of Cookies We Use</h2>
                            <div className="mt-6 grid grid-cols-1 gap-3">
                                {COOKIE_TYPES.map((t) => (
                                    <div key={t.title} className="border border-border bg-card p-5" data-testid={`cookie-type-${t.title.toLowerCase().replace(/\s+/g, "-")}`}>
                                        <div className="flex items-center gap-3">
                                            <h3 className="font-display text-lg font-semibold">{t.title}</h3>
                                            {t.required && (
                                                <span className="inline-flex items-center border border-[hsl(217,91%,45%)]/40 bg-[hsl(217,91%,45%)]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-[hsl(217,91%,45%)]">
                                                    Required
                                                </span>
                                            )}
                                        </div>
                                        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t.body}</p>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Managing */}
                        <section id="section-manage" className="border-b border-border py-8" data-testid="cookies-section-manage">
                            <div className="text-xs uppercase tracking-widest text-muted-foreground">Section 04</div>
                            <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">Managing Cookies</h2>
                            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                                Most web browsers allow you to control or disable cookies through their settings. You may:
                            </p>
                            <ul className="mt-4 space-y-2">
                                {CONTROLS.map((c) => (
                                    <li key={c} className="flex gap-3 text-base text-muted-foreground">
                                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(217,91%,45%)]" />
                                        {c}
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-4 rounded-md border-l-4 border-[hsl(27,96%,55%)] bg-card p-4 text-base leading-relaxed text-foreground">
                                Please note that disabling certain cookies may affect the functionality and performance of SpLink.pro.
                            </p>
                        </section>

                        {/* Changes */}
                        <section id="section-changes" className="border-b border-border py-8" data-testid="cookies-section-changes">
                            <div className="text-xs uppercase tracking-widest text-muted-foreground">Section 05</div>
                            <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">Changes to This Cookie Policy</h2>
                            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                                We may update this Cookie Policy from time to time to reflect changes in our services, legal requirements, or technology. The updated version will be posted on this page with a revised "Last Updated" date.
                            </p>
                        </section>

                        {/* Contact */}
                        <section id="section-contact" className="py-8" data-testid="cookies-section-contact">
                            <div className="text-xs uppercase tracking-widest text-muted-foreground">Section 06</div>
                            <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">Contact Us</h2>
                            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                                If you have questions about this Cookie Policy or our use of cookies, please contact us:
                            </p>
                            <div className="mt-6 border border-border bg-card p-6">
                                <div className="font-display text-lg font-bold">SpLink.pro™</div>
                                <div className="mt-1 text-sm text-muted-foreground">Part of the SocialPulse Ecosystem</div>
                                <div className="mt-4 space-y-2 font-mono text-sm">
                                    <a href="mailto:support@splink.pro" className="flex items-center gap-2 text-primary hover:underline" data-testid="cookies-contact-email">
                                        <Mail className="h-4 w-4" /> support@splink.pro
                                    </a>
                                    <a href="https://splink.pro" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-primary hover:underline" data-testid="cookies-contact-web">
                                        splink.pro
                                    </a>
                                </div>
                            </div>
                        </section>

                        <VersionHistory docKey="cookies" testId="cookies-version-history" />
                    </article>
                </div>
            </section>

            </LegalLanguageSwitcher>

            <footer className="mx-auto max-w-6xl px-6 py-12">
                <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                    <Logo />
                    <div className="flex flex-wrap items-center gap-6 font-mono text-xs text-muted-foreground">
                        <Link to="/about" className="hover:text-foreground">About</Link>
                        <Link to="/terms" className="hover:text-foreground">Terms</Link>
                        <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
                        <Link to="/cookies" className="hover:text-foreground">Cookies</Link>
                        <Link to="/dpa" className="hover:text-foreground">DPA</Link>
                        <span>© {new Date().getFullYear()} SpLink.pro™</span>
                    </div>
                </div>
            </footer>
        </div>
    );
}
